import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { useNotificationPermission } from "./useNotificationPermission";

type NotificationPermissionModalProps = {
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
  autoShow?: boolean;
};

const NotificationPermissionModal = ({
  onPermissionGranted,
  onPermissionDenied,
  autoShow = true,
}: NotificationPermissionModalProps) => {
  const [isRequesting, setIsRequesting] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [showInlineInstructions, setShowInlineInstructions] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    permission,
    isSupported,
    canRequestPermission,
    requestPermission,
    showTestNotification,
    getBrowserInfo,
    checkPermissionStatus,
  } = useNotificationPermission();

  const hasBeenAskedRecently = useCallback(() => {
    const requested = localStorage.getItem("notificationPermissionRequested");
    const requestDate = localStorage.getItem("notificationPermissionDate");

    if (!requested || !requestDate) return false;

    const daysSinceRequest = (Date.now() - new Date(requestDate).getTime()) / (1000 * 60 * 60 * 24);
    return requested === "denied" && daysSinceRequest < 7;
  }, []);

  const showEnableInstructions = useCallback(() => {
    setShowInlineInstructions(true);
  }, []);

  const handlePermissionGranted = useCallback(() => {
    console.log("✅ Notifications enabled successfully!");

    localStorage.setItem("notificationPermissionRequested", "granted");
    localStorage.setItem("notificationPermissionDate", new Date().toISOString());

    onPermissionGranted?.();
  }, [onPermissionGranted]);

  const handlePermissionDenied = useCallback(() => {
    console.log("❌ Notifications denied or blocked");

    localStorage.setItem("notificationPermissionRequested", "denied");
    localStorage.setItem("notificationPermissionDate", new Date().toISOString());

    onPermissionDenied?.();
  }, [onPermissionDenied]);

  const handleRequestPermission = useCallback(async () => {
    if (!isSupported) {
      console.warn("This browser does not support notifications");
      handlePermissionDenied();
      return;
    }

    if (!canRequestPermission) {
      console.log("Cannot request permission programmatically, showing instructions");
      showEnableInstructions();
      return;
    }

    setIsRequesting(true);

    try {
      const result = await requestPermission();

      if (result === "granted") {
        handlePermissionGranted();
        setIsModalOpen(false);

        showTestNotification("Notifications Enabled! 🎉", {
          body: "You'll now receive important updates from Canada Prime Jobs",
        });
      } else {
        handlePermissionDenied();
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      handlePermissionDenied();
    } finally {
      setIsRequesting(false);
    }
  }, [
    isSupported,
    canRequestPermission,
    handlePermissionDenied,
    showEnableInstructions,
    requestPermission,
    handlePermissionGranted,
    showTestNotification,
  ]);

  const shouldShowModal = useCallback(async (): Promise<boolean> => {
    if (hasBeenAskedRecently()) {
      console.log("User was recently asked about notifications, skipping modal");
      return false;
    }

    if (!isSupported) {
      console.log("Notifications not supported, skipping modal");
      return false;
    }

    const currentPermission = await checkPermissionStatus();
    console.log("Current permission status for modal decision:", currentPermission);

    const shouldShow = currentPermission === "default" || currentPermission === "denied";
    console.log("Should show modal:", shouldShow);

    return shouldShow;
  }, [hasBeenAskedRecently, isSupported, checkPermissionStatus]);

  useEffect(() => {
    if (!autoShow || hasShown) {
      return;
    }

    shouldShowModal().then((shouldShow) => {
      if (shouldShow) {
        const delay = permission === "denied" ? 500 : 2000;

        const timer = setTimeout(() => {
          console.log("Opening notification modal");
          setIsModalOpen(true);
          setHasShown(true);
        }, delay);

        return () => clearTimeout(timer);
      }
      return null;
    });
  }, [autoShow, hasShown, shouldShowModal, permission]);

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    handlePermissionDenied();
  }, [handlePermissionDenied]);

  if (!isSupported || permission === "granted") {
    return null;
  }

  const renderHelpToEnableNotifications = (): JSX.Element => {
    const browserInfo = getBrowserInfo();
    switch (browserInfo.name) {
      case "chrome":
        return (
          <div className="space-y-2">
            <p className="text-purple-700 text-xs">For Chrome:</p>
            <ol className="text-purple-700 text-xs space-y-1 ml-4 leading-5 list-decimal">
              <li>
                To the left of the web address, click <strong>"View site information"</strong>
              </li>
              <li>
                Click on <strong>Setting Icon</strong> and then click on <strong>Permissions</strong>
              </li>
              <li>
                Find <strong>"Notifications"</strong> and change to <strong>"Allow"</strong>
              </li>
              <li>Refresh this page</li>
            </ol>
            <p className="text-purple-600 text-xs mt-2">
              <a
                href="https://support.google.com/chrome/answer/114662"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-purple-800 font-semibold"
              >
                Chrome's official guide
              </a>
            </p>
          </div>
        );
      case "firefox":
        return (
          <div className="space-y-2">
            <p className="text-purple-700 text-xs">For Firefox:</p>
            <ol className="text-purple-700 text-xs space-y-1 ml-4 list-decimal">
              <li>Click the 🛡️ shield icon in your address bar</li>
              <li>Select "Permissions"</li>
              <li>Find "Notifications" and change to "Allow"</li>
              <li>Refresh this page</li>
            </ol>
          </div>
        );
      case "safari":
        return (
          <div className="space-y-2">
            <p className="text-purple-700 text-xs">For Safari:</p>
            <ol className="text-purple-700 text-xs space-y-1 ml-4 list-decimal">
              <li>Go to Safari → Preferences</li>
              <li>Click "Websites" tab</li>
              <li>Select "Notifications" from the left sidebar</li>
              <li>Find this site and change to "Allow"</li>
              <li>Refresh this page</li>
            </ol>
          </div>
        );
      case "edge":
        return (
          <div className="space-y-2">
            <p className="text-purple-700 text-xs">For Edge:</p>
            <ol className="text-purple-700 text-xs space-y-1 ml-4 list-decimal">
              <li>Click the 🔒 lock icon in your address bar</li>
              <li>Select "Site permissions"</li>
              <li>Find "Notifications" and change to "Allow"</li>
              <li>Refresh this page</li>
            </ol>
          </div>
        );
      default:
        return (
          <div className="space-y-2">
            <p className="text-purple-700 text-xs">General instructions:</p>
            <ol className="text-purple-700 text-xs space-y-1 ml-4 list-decimal">
              <li>Look for a lock or settings icon in your address bar</li>
              <li>Find site permissions or settings</li>
              <li>Change notifications to "Allow"</li>
              <li>Refresh this page</li>
            </ol>
          </div>
        );
    }
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleClose}
      isDismissable={true}
      size="md"
      placement="center"
      backdrop="blur"
      scrollBehavior="inside"
      hideCloseButton={false}
      classNames={{
        base: "mx-4 max-w-md sm:max-w-lg max-h-[90vh]",
        body: "p-0",
        header: "p-0",
        footer: "p-0",
        backdrop: "bg-black/50 backdrop-blur-sm",
      }}
    >
      <ModalContent className="overflow-hidden max-h-full flex flex-col">
        {/* Header with Icon */}
        <ModalHeader className="flex flex-col items-center text-center pt-8 pb-6 px-6 sm:px-8 bg-gradient-to-b from-gray-50 to-white">
          <div className="relative mb-6">
            <div
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ backgroundColor: "#C71F2C" }}
            >
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
              </svg>
            </div>
            <div className="absolute -top-1 -right-1 w-6 h-6 sm:w-7 sm:h-7 bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>

          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">Stay Updated</h2>
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed max-w-sm">
            Get notified instantly when new job opportunities match your profile
          </p>
        </ModalHeader>

        {/* Body */}
        <ModalBody className="px-6 sm:px-8 py-6 max-h-[50vh] sm:max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 relative">
          <div className="space-y-5 pr-2">
            {/* Benefits */}
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6"
                    style={{ color: "#C71F2C" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base">New Job Alerts</h3>
                  <p className="text-gray-600 text-xs sm:text-sm">When jobs matching your skills are posted</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Application Updates</h3>
                  <p className="text-gray-600 text-xs sm:text-sm">Status changes and interview invitations</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Important Messages</h3>
                  <p className="text-gray-600 text-xs sm:text-sm">From employers and platform updates</p>
                </div>
              </div>
            </div>

            {/* Warning for denied permission */}
            {!canRequestPermission && permission === "denied" && !showInlineInstructions && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="font-medium text-blue-800 text-sm">Notifications Currently Blocked</p>
                    <p className="text-blue-700 text-xs mt-1">
                      Click "Show Instructions" below to learn how to enable notifications in your browser
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Inline Instructions */}
            {showInlineInstructions && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex-1">
                    <p className="font-medium text-purple-800 text-sm mb-3">How to Enable Notifications</p>

                    {renderHelpToEnableNotifications()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </ModalBody>

        {/* Footer */}
        <ModalFooter className="flex flex-col gap-3 p-6 sm:p-8 bg-gray-50">
          <Button
            onPress={handleRequestPermission}
            isLoading={isRequesting}
            className="w-full h-12 font-semibold text-white shadow-lg"
            style={{ backgroundColor: "#C71F2C" }}
            radius="lg"
          >
            {isRequesting ? "Requesting..." : !canRequestPermission ? "Show Instructions" : "Enable Notifications"}
          </Button>

          <Button variant="light" onPress={handleClose} className="w-full h-10 text-gray-600 font-medium" radius="lg">
            Maybe Later
          </Button>

          <p className="text-center text-xs text-gray-500 mt-2">You can change this anytime in your browser settings</p>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NotificationPermissionModal;
